import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { proposalService } from '../../service/proposal.service';
import { propertyService } from '../../service/property.service';
import { ProposalDto } from '../../models/proposal.model';
import { ProposalDetailedDto, ProposalPropertyWithRoomsDto, ProposalRoomDto } from '../../models/proposal-detailed.model';
import { ProposalApprovalDto } from '../../models/proposal-approval.model';
import { Property } from '../../models/property.model';
import PageHeader from '../PageHeader';

// PrimeReact Components
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';

// React Bootstrap Icons
import * as Icon from 'react-bootstrap-icons';

// Component styles
import './ToBeActionedTab.css';

// Helper function to safely get array data from JSON reference or normal array
const getArrayData = (data: any): any[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && '$values' in data && Array.isArray(data.$values)) {
        return data.$values;
    }
    return [];
};

interface ToBeActionedTabProps {
    proposalStatus: string;
    isActive: boolean;
    isReadOnly?: boolean; // If true, hides selection controls and only shows details
}

const ToBeActionedTab: React.FC<ToBeActionedTabProps> = ({ proposalStatus, isActive, isReadOnly = false }) => {
    const [proposals, setProposals] = useState<ProposalDto[]>([]);
    const [selectedProposal, setSelectedProposal] = useState<ProposalDto | null>(null);
    const [detailedProposal, setDetailedProposal] = useState<ProposalDetailedDto | null>(null);
    const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
    const [selectedRooms, setSelectedRooms] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingProperties, setLoadingProperties] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showRoomModal, setShowRoomModal] = useState<boolean>(false);
    const [selectedPropertyForRooms, setSelectedPropertyForRooms] = useState<ProposalPropertyWithRoomsDto | null>(null);
    const [selectedRoomForModal, setSelectedRoomForModal] = useState<any | null>(null);
    const [selectedPropertyForModal, setSelectedPropertyForModal] = useState<ProposalPropertyWithRoomsDto | null>(null);
    const [modalImageIndex, setModalImageIndex] = useState<number>(0);
    // Read raw value from Redux (may be undefined while auth hydrates)
    const rmcFromStore = useSelector((s: any) => s.auth?.rmcId);

    // Local rmcId state persisted to localStorage so page refresh can use the last known RMC ID.
    const [rmcId, setRmcId] = useState<string | null>(() => {
        try {
            return window?.localStorage?.getItem('rmcId') || null;
        } catch (e) {
            return null;
        }
    });

    // When Redux auth provides an rmcId, persist it and use it.
    useEffect(() => {
        if (typeof rmcFromStore !== 'undefined' && rmcFromStore !== null && rmcFromStore !== '') {
            const idStr = String(rmcFromStore);
            setRmcId(idStr);
            try {
                window.localStorage.setItem('rmcId', idStr);
            } catch (e) {
                // ignore storage errors
            }
        }
    }, [rmcFromStore]);

    // Group proposals by clientName for shortlisting
    const groupedEntries = useMemo(() => {
        const map = new Map<string, ProposalDto[]>();
        proposals.forEach((p) => {
            const key = (p?.clientName || 'Unknown').toString();
            if (!map.has(key)) map.set(key, []);
            // avoid duplicates
            if (!map.get(key)!.some(pr => pr.id === p.id)) map.get(key)!.push(p);
        });
        return Array.from(map.entries()); // [ [clientName, proposals[]], ... ]
    }, [proposals]);

    // Hide modal on component mount/remount
    useEffect(() => {
        setShowRoomModal(false);
        setSelectedPropertyForRooms(null);
    }, []);

    useEffect(() => {
        const fetchProposals = async () => {
            // If rmcId is null it means we don't have a persisted RMC ID yet; wait for store or persistence
            if (rmcId == null) {
                return;
            }

            // If rmcId is present but empty treat as missing
            if (rmcId === '') {
                setError('RMC ID not found');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // Use rmcId if needed by service; here service call remains same if it reads from auth on server side
                const response = await proposalService.getProposalListByRMCId(proposalStatus, isActive);
                setProposals(response);
                setError(null);
            } catch (err) {
                console.error('Error fetching proposals:', err);
                setError('Failed to fetch proposals');
            } finally {
                setLoading(false);
            }
        };

        fetchProposals();
    }, [rmcId, proposalStatus, isActive]);

    const handleViewDetails = async (proposal: ProposalDto) => {
        try {
            setLoadingProperties(true);
            setSelectedProposal(proposal);
            const detailedResponse = await proposalService.getDetailedProposal(proposal.id);
            console.log('Detailed proposal response:', detailedResponse);
            setDetailedProposal(detailedResponse);
        } catch (err) {
            console.error('Error fetching detailed proposal:', err);
            setError('Failed to fetch proposal details');
        } finally {
            setLoadingProperties(false);
        }
    };

    const handleBackToProposals = async () => {
        setSelectedProposal(null);
        setDetailedProposal(null);
        setSelectedRooms(new Set());
        setSelectedProperties(new Set());
        setSelectedPropertyForRooms(null);


        // Refresh the proposals list to reflect any changes
        try {
            setLoading(true);
            const response = await proposalService.getProposalListByRMCId(proposalStatus, isActive);
            setProposals(response);
            setError(null);
        } catch (err) {
            console.error('Error refreshing proposals:', err);
            setError('Failed to refresh proposals');
        } finally {
            setLoading(false);
        }
    };

    const handleViewRooms = (property: ProposalPropertyWithRoomsDto) => {
        setSelectedPropertyForRooms(property);
        // setShowRoomModal(true); // Modal disabled
    };

    const handleCloseRoomModal = () => {
        // Close only the modal and clear the selected room preview and reset image index.
        setShowRoomModal(false);
        setSelectedRoomForModal(null);
        setSelectedPropertyForModal(null);
        setModalImageIndex(0);
    };

    // Helper to get a URL string from various room image shapes
    const getImageUrl = (item: any): string | null => {
        if (!item && item !== 0) return null;
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
            if (typeof item.imageUrl === 'string' && item.imageUrl) return item.imageUrl;
            if (typeof item.url === 'string' && item.url) return item.url;
            if (typeof item.image === 'string' && item.image) return item.image;
        }
        return null;
    };

    // Images shown in the modal header carousel (either for a single room preview or aggregated for a property)
    const modalImages = useMemo(() => {
        // Return array of string URLs
        if (selectedRoomForModal) {
            return getArrayData(selectedRoomForModal.roomImages).map(getImageUrl).filter(Boolean) as string[];
        }

        // If property-level preview explicitly selected, prefer propertyImages
        if (selectedPropertyForModal) {
            const propImgs = getArrayData(selectedPropertyForModal.propertyImages).map(getImageUrl).filter(Boolean) as string[];
            if (propImgs.length > 0) return propImgs;

            // fallback to room images aggregated
            return getArrayData(selectedPropertyForModal.rooms)
                .flatMap((r: any) => getArrayData(r.roomImages))
                .map(getImageUrl)
                .filter(Boolean) as string[];
        }

        if (selectedPropertyForRooms) {
            // aggregate images from all rooms in the property
            return getArrayData(selectedPropertyForRooms.rooms)
                .flatMap((r: any) => getArrayData(r.roomImages))
                .map(getImageUrl)
                .filter(Boolean) as string[];
        }

        return [] as string[];
    }, [selectedRoomForModal, selectedPropertyForRooms, selectedPropertyForModal]);

    // Reset carousel index when the images change
    useEffect(() => {
        setModalImageIndex(0);
    }, [modalImages.length, selectedRoomForModal, selectedPropertyForRooms, selectedPropertyForModal]);

    const showPrevImage = () => {
        if (modalImages.length === 0) return;
        setModalImageIndex(i => (i - 1 + modalImages.length) % modalImages.length);
    };

    const showNextImage = () => {
        if (modalImages.length === 0) return;
        setModalImageIndex(i => (i + 1) % modalImages.length);
    };

    // Compute up to 3 visible images for the slider (wraps around)
    const modalVisibleImages = useMemo(() => {
        const len = modalImages.length;
        if (len === 0) return [] as any[];
        const count = Math.min(3, len);
        return Array.from({ length: count }, (_, i) => modalImages[(modalImageIndex + i) % len]);
    }, [modalImages, modalImageIndex]);

    const modalImageRangeLabel = useMemo(() => {
        const len = modalImages.length;
        if (len === 0) return '0 / 0';
        const start = (modalImageIndex % len) + 1;
        const end = ((modalImageIndex + modalVisibleImages.length - 1) % len) + 1;
        if (modalVisibleImages.length === len) return `1 / ${len}`;
        if (start <= end) return `${start}-${end} / ${len}`;
        // wrapped range e.g. 5-2 => show 5-N,1-2
        return `${start}-${len},1-${end} / ${len}`;
    }, [modalImages, modalImageIndex, modalVisibleImages.length]);

    const handlePropertySelection = (propertyId: string | number) => {
        setSelectedProperties(prev => {
            const newSet = new Set(prev);
            const id = propertyId.toString();
            if (newSet.has(id)) {
                newSet.delete(id);
                // Also remove all rooms from this property when deselecting property
                setSelectedRooms(prevRooms => {
                    const newRoomSet = new Set(prevRooms);
                    // Find all rooms from this property and remove them
                    const allProperties = getArrayData(detailedProposal?.properties) || [];
                    const property = allProperties.find(p => p.propertyId.toString() === id);
                    if (property) {
                        getArrayData(property.rooms).forEach(room => {
                            newRoomSet.delete(room.roomId.toString());
                        });
                    }
                    return newRoomSet;
                });
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleRoomSelection = (roomId: string | number) => {
        // First check if the property containing this room is selected
        const roomProperty = selectedPropertyForRooms;
        if (!roomProperty || !selectedProperties.has(roomProperty.propertyId.toString())) {
            // Property is not selected, don't allow room selection
            return;
        }

        setSelectedRooms(prev => {
            const newSet = new Set(prev);
            const id = roomId.toString();
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    // Helper function to select all rooms in a property
    const handleSelectAllRoomsInProperty = (property: ProposalPropertyWithRoomsDto) => {
        // Check if the property is selected first
        if (!selectedProperties.has(property.propertyId.toString())) {
            // Property is not selected, don't allow room selection
            return;
        }

        const roomIds = getArrayData(property.rooms).map(room => room.roomId.toString());
        setSelectedRooms(prev => {
            const newSet = new Set(prev);
            roomIds.forEach(roomId => newSet.add(roomId));
            return newSet;
        });
    };

    // Helper function to deselect all rooms in a property
    const handleDeselectAllRoomsInProperty = (property: ProposalPropertyWithRoomsDto) => {
        const roomIds = getArrayData(property.rooms).map(room => room.roomId.toString());
        setSelectedRooms(prev => {
            const newSet = new Set(prev);
            roomIds.forEach(roomId => newSet.delete(roomId));
            return newSet;
        });
    };

    const handleApproveProposal = async () => {
        if (!selectedProposal || selectedProperties.size === 0) {
            return;
        }

        try {
            setLoading(true);

            // Only include roomIds if we have selected rooms AND they belong to selected properties
            const selectedPropertyIds = Array.from(selectedProperties);
            const allSelectedProperties = getArrayData(detailedProposal?.properties).filter(prop =>
                selectedPropertyIds.includes(prop.propertyId.toString())
            );

            // Get all room IDs from selected properties
            const allRoomsInSelectedProperties = allSelectedProperties.flatMap(prop =>
                getArrayData(prop.rooms).map(room => room.roomId.toString())
            );

            // Filter selected rooms to only include those from selected properties
            const validSelectedRooms = Array.from(selectedRooms).filter(roomId =>
                allRoomsInSelectedProperties.includes(roomId)
            );

            const approvalData: ProposalApprovalDto = {
                proposalId: selectedProposal.id,
                propertyIds: selectedPropertyIds.map(id => parseInt(id)),
                roomIds: validSelectedRooms.length > 0 ? validSelectedRooms.map(id => parseInt(id)) : undefined
            };

            console.log('Submitting approval for:', approvalData);

            const response = await proposalService.approveProposal(approvalData);

            // Debug: Log the actual response structure
            console.log('Approval response received:', response);
            console.log('Response type:', typeof response);
            console.log('Response keys:', Object.keys(response || {}));

            // Handle string response from API
            if (typeof response === 'string' && response === 'OK') {
                console.log('Proposal approved successfully');
                handleBackToProposals();
            } else if (response && typeof response === 'object' && response.isSuccess) {
                console.log('Proposal approved successfully:', response.message);
                handleBackToProposals();
            } else {
                setError(`Failed to approve proposal: ${typeof response === 'string' ? response : response?.message || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Error approving proposal:', err);
            setError('Failed to approve proposal');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to validate that selected properties have at least one room selected
    const isSubmitEnabled = () => {
        if (selectedProperties.size === 0 || loading) return false;

        // Check if for every selected property, at least one room is selected
        const selectedPropertyIds = Array.from(selectedProperties);
        for (const propertyId of selectedPropertyIds) {
            const property = getArrayData(detailedProposal?.properties).find(
                p => p.propertyId.toString() === propertyId
            );

            if (property) {
                const propertyRoomIds = getArrayData(property.rooms).map(room => room.roomId.toString());
                const hasSelectedRoom = propertyRoomIds.some(roomId => selectedRooms.has(roomId));

                if (!hasSelectedRoom) {
                    return false; // Property selected but no rooms selected
                }
            }
        }

        return true;
    };

    // Helper function to format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    };

    // Helper function to count properties in proposal
    const getPropertyCount = (proposal: ProposalDto) => {
        if (!proposal.cityProperties) return 0;
        return proposal.cityProperties.reduce((total: number, city: any) => total + city.propertyIds.length, 0);
    };

    // Show property details view
    if (selectedProposal) {
        if (loadingProperties) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" />
                </div>
            );
        }

        return (
            <div>
                <div className="content-body">
                    <div className="container">
                        <div className="row shadow">
                            <div className="col-12">
                                <div className="card border-0">
                                    <div className="card-body pt-2 pb-5">
                                        {/* Proposal Information Header */}
                                        <div className="mb-4 px-2">
                                            <div className="row">
                                                <div className="col-lg-8">
                                                    <h4 className="mb-2">{selectedProposal.name}</h4>
                                                    <div className="mb-2">
                                                        <small className="text-muted">
                                                            <i className="pi pi-calendar me-1"></i>
                                                            Start Date: {formatDate(selectedProposal.startDate)}
                                                        </small>
                                                    </div>
                                                    {selectedProposal.description && (
                                                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                                            {selectedProposal.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="col-lg-4 text-end">
                                                    <div className="d-flex flex-column align-items-end gap-2">
                                                        <Badge
                                                            value={`${getArrayData(detailedProposal?.properties).length || 0} Properties`}
                                                            severity="info"
                                                            size="large"
                                                        />
                                                        {/* <Badge
                                                            value={`${Array.from(selectedProperties).length} Selected${selectedRooms.size > 0 ? ` (${selectedRooms.size} rooms)` : ''}`}
                                                            severity={selectedProperties.size > 0 ? "success" : "secondary"}
                                                        /> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Properties Section */}
                                        <div className="wizard-content">
                                            <div className="wizard-step">
                                                <div className="row">
                                                    {/* Left Panel - Available Properties */}
                                                    <div className="col-md-6">
                                                        <div className="properties-panel">
                                                            <h5 className="mb-3">
                                                                <i className="pi pi-building me-2"></i>
                                                                Available Properties
                                                            </h5>

                                                            {getArrayData(detailedProposal?.properties).length > 0 ? (
                                                                <div className="properties-list">
                                                                    {getArrayData(detailedProposal?.properties).map((property) => (
                                                                        <div
                                                                            key={property.propertyId}
                                                                            className={`property-item ${selectedPropertyForRooms?.propertyId === property.propertyId ? 'selected' : ''}`}
                                                                            onClick={() => handleViewRooms(property)}
                                                                        >
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                {!isReadOnly && (
                                                                                    <Checkbox
                                                                                        inputId={`property-${property.propertyId}`}
                                                                                        checked={selectedProperties.has(property.propertyId?.toString() || '')}
                                                                                        onChange={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handlePropertySelection(property.propertyId || '');
                                                                                        }}
                                                                                    />
                                                                                )}

                                                                                <div className="property-image-small">
                                                                                    {getArrayData(property.propertyImages).length > 0 ? (
                                                                                        <img
                                                                                            src={getArrayData(property.propertyImages)[0]}
                                                                                            alt={property.propertyName}
                                                                                            onError={(e) => {
                                                                                                const target = e.target as HTMLImageElement;
                                                                                                target.src = '/Images/no-image-placeholder.png';
                                                                                            }}
                                                                                        />
                                                                                    ) : (
                                                                                        <div className="property-image-placeholder">
                                                                                            <i className="pi pi-image"></i>
                                                                                        </div>
                                                                                    )}
                                                                                </div>

                                                                                <div className="property-info flex-grow-1">
                                                                                    <h6 className="property-name mb-1">{property.propertyName}</h6>
                                                                                    <div className="property-location">
                                                                                        <i className="pi pi-map-marker me-1"></i>
                                                                                        <small className="text-muted">
                                                                                            {property.address?.substring(0, 30) || property.cityName}...
                                                                                        </small>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="property-actions-small">
                                                                                    <Button
                                                                                        icon="pi pi-eye"
                                                                                        className="p-button-text p-button-sm"
                                                                                        tooltip="Preview"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                // Open property-level preview modal
                                                                                                setSelectedPropertyForModal(property);
                                                                                                setSelectedRoomForModal(null);
                                                                                                setShowRoomModal(true);
                                                                                            }}
                                                                                    />
                                                                                    <Button
                                                                                        icon="pi pi-th-large"
                                                                                        className="p-button-text p-button-sm"
                                                                                        tooltip="View Rooms"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleViewRooms(property);
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center text-muted py-5">
                                                                    <i className="pi pi-building" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                                                    <p className="mt-3 mb-0">No properties available for this proposal.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Right Panel - Room Details */}
                                                    <div className="col-md-6">
                                                        <div className="rooms-panel">
                                                            {selectedPropertyForRooms ? (
                                                                <>
                                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                                        <h5 className="mb-0">
                                                                            <i className="pi pi-th-large me-2"></i>
                                                                            {selectedPropertyForRooms.propertyName} - Rooms
                                                                        </h5>
                                                                        <Button
                                                                            icon="pi pi-arrow-left"
                                                                            label="Back"
                                                                            className="p-button-text p-button-sm"
                                                                            onClick={() => setSelectedPropertyForRooms(null)}
                                                                        />
                                                                    </div>
                                                                    {!isReadOnly && (
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            {!selectedProperties.has(selectedPropertyForRooms?.propertyId?.toString() || '') && (
                                                                                <div className="mt-1">
                                                                                    <small className="text-warning">
                                                                                        <i className="pi pi-exclamation-triangle me-1"></i>
                                                                                        Select the property first to enable room selection
                                                                                    </small>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {!isReadOnly && (
                                                                        <div className="property-summary mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <div>
                                                                                <small className="text-muted">
                                                                                    {getArrayData(selectedPropertyForRooms.rooms).length} room{getArrayData(selectedPropertyForRooms.rooms).length !== 1 ? 's' : ''} • {getArrayData(selectedPropertyForRooms.rooms).filter(room => selectedRooms.has(room.roomId.toString())).length} selected
                                                                                </small>
                                                                            </div>
                                                                            <div className="d-flex gap-2">
                                                                                <Button
                                                                                    label="Select All"
                                                                                    size="small"
                                                                                    className="p-button-text p-button-sm"
                                                                                    onClick={() => handleSelectAllRoomsInProperty(selectedPropertyForRooms)}
                                                                                    disabled={!selectedProperties.has(selectedPropertyForRooms?.propertyId?.toString() || '')}
                                                                                    tooltip={!selectedProperties.has(selectedPropertyForRooms?.propertyId?.toString() || '') ? "Select the property first" : "Select all rooms in this property"}
                                                                                    tooltipOptions={{ position: 'top' }}
                                                                                />
                                                                                <Button
                                                                                    label="Deselect All"
                                                                                    size="small"
                                                                                    className="p-button-text p-button-sm"
                                                                                    onClick={() => handleDeselectAllRoomsInProperty(selectedPropertyForRooms)}
                                                                                    disabled={!selectedProperties.has(selectedPropertyForRooms?.propertyId?.toString() || '')}
                                                                                    tooltip={!selectedProperties.has(selectedPropertyForRooms?.propertyId?.toString() || '') ? "Select the property first" : "Deselect all rooms in this property"}
                                                                                    tooltipOptions={{ position: 'top' }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    )}

                                                                    <div className="rooms-grid">
                                                                        {getArrayData(selectedPropertyForRooms.rooms).map((room) => {
                                                                            const isPropertySelected = selectedProperties.has(selectedPropertyForRooms?.propertyId?.toString() || '');
                                                                            const isRoomSelected = selectedRooms.has(room.roomId.toString());

                                                                            return (
                                                                                <div key={room.roomId} className={`room-card-small mb-3 ${!isReadOnly && isRoomSelected ? 'selected' : ''} ${!isReadOnly && !isPropertySelected ? 'disabled' : ''}`}>
                                                                                    <div className="card">
                                                                                        <div className="card-body p-3">
                                                                                            <div className="d-flex gap-3">
                                                                                                <div className="d-flex align-items-start gap-3">
                                                                                                    {!isReadOnly && (
                                                                                                        <Checkbox
                                                                                                            inputId={`room-${room.roomId}`}
                                                                                                            checked={selectedRooms.has(room.roomId?.toString() || '')}
                                                                                                            onChange={() => handleRoomSelection(room.roomId || '')}
                                                                                                            className="mt-1"
                                                                                                            disabled={!isPropertySelected}
                                                                                                            tooltip={!isPropertySelected ? "Select the property first to enable room selection" : ""}
                                                                                                            tooltipOptions={{ position: 'top' }}
                                                                                                        />
                                                                                                    )}

                                                                                                    <div className="room-image-container">
                                                                                                        {(room.roomImages && getArrayData(room.roomImages).length > 0) ? (
                                                                                                            <img
                                                                                                                src={getArrayData(room.roomImages)[0]}
                                                                                                                alt={room.roomName}
                                                                                                                className="room-image"
                                                                                                            />
                                                                                                        ) : (
                                                                                                            <img
                                                                                                                src={`/Images/room${((room.roomId % 12) + 1)}.jpg`}
                                                                                                                alt={room.roomName}
                                                                                                                className="room-image"
                                                                                                            />
                                                                                                        )}
                                                                                                    </div>
                                                                                                    <div className="room-actions ms-auto">
                                                                                                        <Button
                                                                                                            icon="pi pi-eye"
                                                                                                            className="p-button-text p-button-sm"
                                                                                                            tooltip="Preview Room"
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                setSelectedRoomForModal(room);
                                                                                                                setShowRoomModal(true);
                                                                                                            }}
                                                                                                            aria-label={`Preview room ${room.roomName || room.roomId}`}
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="room-details flex-grow-1">
                                                                                                    <h6 className="room-name mb-1">{room.roomName}</h6>
                                                                                                    <p className="room-description mb-2">
                                                                                                        {room.roomTypeName} with {room.numberOfBedrooms} bedroom{room.numberOfBedrooms !== 1 ? 's' : ''} and attached restroom
                                                                                                    </p>

                                                                                                    <div className="room-specs d-flex gap-3">
                                                                                                        <span className="spec-item">
                                                                                                            <i className="pi pi-expand me-1"></i>
                                                                                                            {room.area} sq ft
                                                                                                        </span>
                                                                                                        <span className="spec-item">
                                                                                                            <i className="pi pi-home me-1"></i>
                                                                                                            {room.numberOfBedrooms} Bedroom{room.numberOfBedrooms !== 1 ? 's' : ''}
                                                                                                        </span>
                                                                                                    </div>

                                                                                                    {/* RMC Charges Display */}
                                                                                                    {detailedProposal?.isShowPrice && getArrayData(room.rmcRoomCharges).length > 0 && (
                                                                                                        <div className="room-charges mt-2">
                                                                                                            <small className="text-success fw-bold">
                                                                                                                {detailedProposal?.currencySymbol || '₹'}{getArrayData(room.rmcRoomCharges)[0]?.amount?.toLocaleString() || '0'}/month
                                                                                                            </small>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="text-center text-muted py-5">
                                                                    <i className="pi pi-th-large" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                                                    <p className="mt-3 mb-0">Select a property to view its rooms</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Validation Message */}
                                        {!isReadOnly && selectedProperties.size > 0 && !isSubmitEnabled() && !loading && (
                                            <div className="alert alert-warning mt-3" role="alert">
                                                <i className="pi pi-exclamation-triangle me-2"></i>
                                                Please select at least one room for each selected property to enable submission.
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="wizard-footer mt-4 pt-3 border-top">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <Button
                                                    label="Back to Proposals"
                                                    icon="pi pi-arrow-left"
                                                    outlined
                                                    onClick={handleBackToProposals}
                                                />
                                                {!isReadOnly && (
                                                    <Button
                                                        label={`Submit${selectedProperties.size > 0 ? ` (${selectedProperties.size} ${selectedProperties.size === 1 ? 'property' : 'properties'}${selectedRooms.size > 0 ? `, ${selectedRooms.size} ${selectedRooms.size === 1 ? 'room' : 'rooms'}` : ''})` : ''}`}
                                                        icon="pi pi-check"
                                                        size="large"
                                                        className="p-button-success"
                                                        disabled={!isSubmitEnabled()}
                                                        loading={loading}
                                                        onClick={handleApproveProposal}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Room Details Modal */}
                <Dialog
                    visible={showRoomModal}
                    onHide={handleCloseRoomModal}
                    header={`${selectedPropertyForModal ? 'Property Preview - ' + (selectedPropertyForModal.propertyName || '') : 'Room Details - ' + (selectedPropertyForRooms?.propertyName || selectedRoomForModal?.roomName || '')}`}
                    style={{ width: '90vw', maxWidth: '1200px' }}
                    modal
                    draggable={false}
                    resizable={false}
                >
                    {/* Image slider below header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                        <Button icon="pi pi-chevron-left" className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); showPrevImage(); }} aria-label="Previous image" />

                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            {modalVisibleImages && modalVisibleImages.length > 0 ? (
                                <div style={{ width: '100%', maxHeight: 320, overflow: 'hidden', borderRadius: 8, backgroundColor: '#f3f4f6', display: 'flex', gap: 8, padding: 4 }}>
                                    {modalVisibleImages.map((imgSrc, idx) => (
                                        <div key={idx} style={{ flex: 1, overflow: 'hidden', borderRadius: 6, backgroundColor: '#fff' }}>
                                            <img
                                                src={imgSrc}
                                                alt={`Slide ${modalImageIndex + idx + 1}`}
                                                style={{ width: '100%', height: '320px', objectFit: 'cover', display: 'block' }}
                                                onError={(e) => { (e.currentTarget as HTMLImageElement).src = `${process.env.PUBLIC_URL || ''}/Images/no-image-placeholder.png`; }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ width: '100%', maxHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', backgroundColor: '#f3f4f6', borderRadius: 8, padding: '1rem' }}>
                                    <i className="pi pi-image" style={{ fontSize: '1.5rem' }} />
                                </div>
                            )}
                        </div>

                        <Button icon="pi pi-chevron-right" className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); showNextImage(); }} aria-label="Next image" />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: '0.75rem' }} role="tablist" aria-label="Image pagination">
                        {modalImages && modalImages.length > 0 ? (
                            modalImages.map((_, idx) => {
                                const isActive = idx === modalImageIndex;
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setModalImageIndex(idx)}
                                        aria-label={`Go to image ${idx + 1}`}
                                        aria-current={isActive}
                                        style={{
                                            width: isActive ? 18 : 8,
                                            height: 8,
                                            borderRadius: 12,
                                            backgroundColor: isActive ? '#2563eb' : '#6b7280',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            transition: 'width 150ms ease-in-out, background-color 150ms ease-in-out'
                                        }}
                                    />
                                );
                            })
                        ) : (
                            <div style={{ width: 8, height: 8, borderRadius: 8, backgroundColor: '#9ca3af' }} />
                        )}
                    </div>

                    {
                        // If a single room is selected for preview, render only that room
                        selectedRoomForModal ? (
                            <div className="room-details-content">
                                <Card className="room-card" style={{ height: 'fit-content' }}>
                                    <div className="room-header" style={{ marginBottom: '1rem' }}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6 className="mb-1">{selectedRoomForModal.roomName}</h6>
                                                <div className="d-flex align-items-center">
                                                    <i className="bi bi-door-closed me-2" aria-hidden="true" style={{ fontSize: '1rem' }}></i>
                                                    <small className="text-muted">{selectedRoomForModal.roomTypeName}</small>
                                                </div>
                                            </div>
                                            <Tag
                                                value={`${selectedRoomForModal.area} sq ft`}
                                                severity="secondary"
                                                style={{ fontSize: '0.7rem' }}
                                            />
                                        </div>
                                    </div>

                                    {selectedRoomForModal.roomDescription && (
                                        <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                                            {selectedRoomForModal.roomDescription}
                                        </p>
                                    )}

                                    <div className="room-specs room-specs-inline" style={{ marginBottom: '1rem' }}>
                                        <div className="spec-inline" title="Bedrooms">
                                            <i className="bi bi-house-door-fill icon" aria-hidden="true"></i>
                                            <div className="spec-text">
                                                <span className="value">{selectedRoomForModal.numberOfBedrooms}</span>
                                                <span className="label">Bedrooms</span>
                                            </div>
                                        </div>

                                        <div className="spec-inline" title="Bathrooms">
                                            <i className="bi bi-circle icon" aria-hidden="true"></i>
                                            <div className="spec-text">
                                                <span className="value">{selectedRoomForModal.numberOfBathrooms}</span>
                                                <span className="label">Bathrooms</span>
                                            </div>
                                        </div>

                                        <div className="spec-inline" title="Inventory">
                                            <i className="bi bi-box-seam icon" aria-hidden="true"></i>
                                            <div className="spec-text">
                                                <span className="value">{selectedRoomForModal.totalInventory}</span>
                                                <span className="label">Inventory</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* RMC Room Charges */}
                                    {detailedProposal?.isShowPrice && getArrayData(selectedRoomForModal.rmcRoomCharges).length > 0 && (
                                        <div className="rmc-charges" style={{ marginBottom: '1rem' }}>
                                            <h6 className="mb-2" style={{ fontSize: '0.9rem' }}>
                                                Charges
                                            </h6>
                                            {getArrayData(selectedRoomForModal.rmcRoomCharges).map((charge: any, index: number) => (
                                                <div key={charge.id || index} className="charge-item d-flex justify-content-between align-items-center" style={{
                                                    backgroundColor: '#f0fdf4',
                                                    padding: '0.5rem',
                                                    borderRadius: '4px',
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    <span className="charge-amount fw-bold">{detailedProposal?.currencySymbol || '₹'}{charge.amount.toLocaleString()}</span>
                                                    {charge.taxPercentage && (
                                                        <span className="charge-tax text-muted">
                                                            Tax: {charge.taxPercentage}%
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* RMC Complementary Services */}
                                    {getArrayData(selectedRoomForModal.rmcRoomComplementaries).length > 0 && (
                                        <div className="rmc-complementaries">
                                            <h6 className="mb-2" style={{ fontSize: '0.9rem' }}>
                                                <i className="pi pi-gift me-1"></i>
                                                Complementary Services
                                            </h6>
                                            <div className="complementaries-list-grid">
                                                {getArrayData(selectedRoomForModal.rmcRoomComplementaries).map((comp: any, index: number) => (
                                                    <div key={comp.id || index} className="complementary-item">
                                                        <i className="bi bi-check-circle-fill text-success me-2" aria-hidden="true"></i>
                                                        <span className="complementary-label">{comp.complementaryName}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        ) : (
                            (selectedPropertyForModal || selectedPropertyForRooms) && (
                                (() => {
                                    const prop = selectedPropertyForModal || selectedPropertyForRooms as ProposalPropertyWithRoomsDto;
                                    return (
                                        <div className="room-details-content">
                                            <div className="property-info mb-4" style={{
                                                backgroundColor: '#f8f9fa',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                marginBottom: '1rem'
                                            }}>
                                                <div className="row">
                                                    <div className="col-md-8">
                                                        <h6 className="mb-1">{prop.propertyName}</h6>
                                                        <p className="text-muted mb-1">
                                                            <i className="pi pi-map-marker me-1"></i>
                                                            {prop.address || prop.cityName}
                                                        </p>
                                                        <small className="text-muted">Type: {prop.propertyTypeName}</small>
                                                    </div>
                                                    <div className="col-md-4 text-end">
                                                        <Badge
                                                            value={`${getArrayData(prop.rooms).length} Rooms`}
                                                            severity="info"
                                                            size="large"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Amenities (Complementary Services) */}
                                            <div className="property-attributes mb-3">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <h6 className="mb-2" style={{ fontSize: '0.95rem' }}>
                                                            <i className="pi pi-gift me-2" />
                                                            Amenities 
                                                        </h6>

                                                        {getArrayData((prop as any).amenities).length > 0 ? (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem' }}>
                                                                {getArrayData((prop as any).amenities).map((am: any) => (
                                                                    <div key={am.id || am.name} className="d-flex align-items-center" style={{ gap: 8 }}>
                                                                        <i className="bi bi-check-circle-fill text-success me-2" style={{ fontSize: '1rem' }} aria-hidden="true" />
                                                                        <div>
                                                                            <div style={{ color: '#059669', fontSize: '0.95rem' }}>{am.name}</div>
                                                                            {am.description ? <small className="text-muted">{am.description}</small> : null}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <small className="text-muted">No complementary services configured</small>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="row mt-3">
                                                    <div className="col-12">
                                                        <h6 className="mb-2" style={{ fontSize: '0.95rem' }}>
                                                            <i className="pi pi-th-large me-2" />
                                                            Facilities
                                                        </h6>

                                                        {getArrayData((prop as any).facilities).length > 0 ? (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                                                                {getArrayData((prop as any).facilities).map((f: any) => (
                                                                    <div key={f.facilityId || f.id || f.name} className="d-flex align-items-center">
                                                                        <i className="bi bi-check-circle-fill text-success me-2" style={{ fontSize: '0.9rem' }} aria-hidden="true" />
                                                                        <small style={{ color: '#059669' }}>{f.name || f.facilityName}</small>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <small className="text-muted">No facilities configured</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                                                <h6 className="mb-2"><i className="bi bi-door-closed me-2" aria-hidden="true" />Rooms</h6>
                                            </div>

                                            <div className="rooms-summary-grid" style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                                gap: '0.75rem'
                                            }}>
                                              
                                                {getArrayData(prop.rooms).map((room) => (
                                                    <div key={room.roomId} className="room-summary p-2 border rounded" style={{ background: '#fff' }}>
                                                        <div className="d-flex flex-column">
                                                            <div className="fw-semibold small" style={{ color: '#111' }}>{room.roomTypeName || (room.roomType && room.roomType.name) || 'Type'}</div>
                                                            <div className="text-muted small">{room.roomName || room.categoryName || 'Category not set'}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {getArrayData(prop.rooms).length === 0 && (
                                                <div className="text-center text-muted" style={{ padding: '2rem' }}>
                                                    <i className="pi pi-home" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                                    <p className="mt-3 mb-0">No rooms available for this property</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()
                            )
                        )
                    }
                </Dialog>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="to-be-actioned-tab">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="to-be-actioned-tab">
                <div className="p-3">
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700">
                        <i className="pi pi-exclamation-triangle mr-2"></i>
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    if (proposals.length === 0) {
        return (
            <div className="to-be-actioned-tab">
                <div className="text-center text-muted" style={{ padding: '50px 0' }}>
                    No proposals to be actioned found.
                </div>
            </div>
        );
    }

    return (
        <div className="to-be-actioned-tab">
            {groupedEntries.map(([cityName, cityProposals]) => (
                <div key={cityName} className="proposal-section mb-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-12">
                                <div className="card border-0 city-card">
                                    <div className="card-body p-3">
                                        <div className="section-header mb-2 d-flex justify-content-between align-items-center">
                                            <h6 className="section-title mb-0">{cityName}</h6>
                                            <div className="section-properties-count">{cityProposals.length} proposal{cityProposals.length !== 1 ? 's' : ''}</div>
                                        </div>

                                        <div className="proposal-grid" style={{ marginTop: 0 }}>
                                            {cityProposals.map((proposal) => (
                                                <div key={proposal.id} className="proposal-card">
                                                    <div className="proposal-header">
                                                        <div className="proposal-title">
                                                            {proposal.name} ({getPropertyCount(proposal)})
                                                        </div>
                                                        <div className="proposal-date">{formatDate(proposal.startDate)}</div>
                                                    </div>
                                                    {proposal.clientName && (
                                                        <div className="proposal-client" style={{
                                                            color: '#6c757d',
                                                            fontSize: '0.95rem',
                                                            marginBottom: '6px'
                                                        }}>
                                                            {proposal.clientName}
                                                        </div>
                                                    )}
                                                    {proposal.description && (
                                                        <div className="proposal-description" style={{
                                                            color: '#6c757d',
                                                            fontSize: '0.8rem',
                                                            marginBottom: '8px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {proposal.description}
                                                        </div>
                                                    )}
                                                    <div className="proposal-actions">
                                                        <Button
                                                            label="View Details"
                                                            size="small"
                                                            className="p-button-primary view-details-btn"
                                                            onClick={() => handleViewDetails(proposal)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ToBeActionedTab;
